/**
 * QuickRepliesPage — Brief 4 (Gestor) · Tela Quick Replies.
 *
 * Rota: /admin/ia/quick-replies. Task 9 (Respostas Prontas S1) introduz a
 * biblioteca real (`LibraryManagerView`) por trás de um **gate-and-split**:
 * com `canned-response-manage` a rota passa a servir a biblioteca nova; sem a
 * cap (inércia de produção — cap opt-in), renderiza o catálogo LEGADO
 * (`LegacyQuickRepliesCatalog`, componente local extraído INTACTO do que esta
 * página fazia antes) — o mesmo catálogo cross-playbook agregado de
 * `GET /v1/playbooks`. `quick-replies.spec.tsx` cobre o legado (via
 * `QuickRepliesCatalogView`/`aggregate`) e continua verde sem mudança.
 *
 * **B1 (Blocker revisão final) — o split é SERVER-AUTHORITATIVE, não só
 * client-side:** `GET /v1/auth/me` (`auth.service.ts resolveCapabilitiesForUser`)
 * NÃO filtra `optInRequired` — é um buraco sistêmico PRÉ-EXISTENTE (não
 * introduzido por esta Task), flag ao TL. Resultado: `canned-response-manage`
 * chega `true` no FE para qualquer supervisor/gestor/admin de produção, MESMO
 * sem o `CapabilityEntry` opt-in ativo no tenant. O Engine, por outro lado, É
 * fail-closed no endpoint real (`GET /v1/canned-responses` exige a entry
 * ativa) — sem este fallback, a cap mentirosa do FE prenderia o gestor num
 * 403 sem saída (perderia o catálogo legado; viola "merge não muda
 * produção"). Por isso `LibraryManagerGate` roda a MESMA query que a
 * `LibraryManagerView` (React Query dedupa pela queryKey — zero fetch
 * duplicado) e só entrega a biblioteca nova quando o SERVIDOR confirma
 * (ausência de 403); um 403 real cai no legado silenciosamente.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { LibraryManagerView } from '../features/canned-responses/LibraryManagerView';
import { usePlaybookList } from '../features/playbooks/use-playbooks';
import { QuickRepliesCatalogView } from '../features/quick-replies/QuickRepliesCatalogView';
import { QuickReplyEditModal } from '../features/quick-replies/QuickReplyEditModal';
import { useCannedResponses } from '../hooks/use-canned-responses';
import { useCapability } from '../hooks/use-capability';
import { ApiError } from '../lib/api-client';

import '../features/quick-replies/quick-replies.css';

export function QuickRepliesPage(): JSX.Element {
  const canManageLibrary = useCapability('canned-response-manage');
  if (canManageLibrary) return <LibraryManagerGate />;
  return <LegacyQuickRepliesCatalog />;
}

/**
 * Gate server-authoritative (vide doc-comment do módulo acima). Dispara a
 * MESMA query (`useCannedResponses`) que a `LibraryManagerView` roda —
 * React Query dedupa pela queryKey, então não há 2º fetch quando a view
 * renderiza em seguida. Decide com base na resposta REAL do GET:
 *   - 403 (servidor nega — optInRequired não filtrado no /me) → legado,
 *     silenciosamente (fallback-on-denied; não é um erro de produto).
 *   - loading → cai para `LibraryManagerView`, que já tem seu próprio
 *     spinner (breve; aceitável).
 *   - sucesso ou outro erro (ex. 500) → `LibraryManagerView` normal (o 500
 *     continua com o estado de erro honesto + retry já existente).
 */
function LibraryManagerGate(): JSX.Element {
  const listQ = useCannedResponses(true);
  if (listQ.isError && listQ.error instanceof ApiError && listQ.error.status === 403) {
    return <LegacyQuickRepliesCatalog />;
  }
  return <LibraryManagerView />;
}

/**
 * Conteúdo ATUAL da página antes da Task 9 — extraído intacto (gate-and-split).
 * Serve a rota quando o tenant NÃO tem `canned-response-manage` (cap opt-in
 * da biblioteca nova).
 */
function LegacyQuickRepliesCatalog(): JSX.Element {
  const canRead = useCapability('playbook-read');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="quick-replies-screen quick-replies-screen--gate">
        <CapabilityLockedBanner
          capability="playbook-read"
          tierMin="professional"
          fallbackMessage="O catálogo de quick replies é restrito a perfis de gestão — fale com o administrador do tenant."
        />
      </div>
    );
  }

  return <LegacyQuickRepliesCatalogInner />;
}

function LegacyQuickRepliesCatalogInner(): JSX.Element {
  const canAuthor = useCapability('playbook-author');
  const list = usePlaybookList();
  const [editingPlaybookId, setEditingPlaybookId] = useState<string | null>(null);

  if (list.isLoading) {
    return (
      <div className="quick-replies-screen">
        <div className="qr-panel">
          <p className="qr-muted">Carregando quick replies…</p>
        </div>
      </div>
    );
  }

  if (list.isError) {
    return (
      <div className="quick-replies-screen">
        <div className="qr-panel">
          <p className="qr-alert" role="alert">
            Falha ao carregar os playbooks.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <QuickRepliesCatalogView
        playbooks={list.data}
        canAuthor={canAuthor}
        onEdit={(playbookId) => {
          setEditingPlaybookId(playbookId);
        }}
      />
      {editingPlaybookId !== null && (
        <QuickReplyEditModal
          playbookId={editingPlaybookId}
          onClose={() => {
            setEditingPlaybookId(null);
          }}
        />
      )}
    </>
  );
}
